<template>
  <div
    class="klient-item"
    :class="{'admin-client-item': role === 'alex_admin'}"
  >
    <lead-timeline v-if="role === 'alex_admin' || role === 'financnik'" :profile-id="clientData.client_profile.id" />
    <div class="klient-item-to-part">
      <div class="client-summary">
        <div class="klient-info-wrapper">
          <div class="client-badges">
            <a
              v-if="parsedSource.href || parsedSource.className === 'source-ads'"
              class="source-badge"
              :class="parsedSource.className"
              :href="parsedSource.href"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ parsedSource.label }}
            </a>
            <client-district-badges :client="clientData" />
          </div>

          <div
            class="client-identity-row"
            :class="{'client-identity-row--admin': role === 'alex_admin'}"
          >
            <div v-if="role === 'alex_admin'" class="assigned-broker">
              <span class="identity-kicker">Finančník</span>
              <div class="assigned-broker-avatar">
                <broker-picker-handler
                  :rules="['priority']"
                  :broker-id="clientData.financnik"
                  :client-info="clientData"
                  clients
                  klient
                />
              </div>
            </div>

            <div class="client-copy">
              <span v-if="role === 'alex_admin'" class="identity-kicker">Klient</span>
              <user-manager-handler :rules="['priority']" :client-info="clientData" client clients telephone/>
            </div>
            <day-badge
              v-if="showDayBadge && clientData.client_profile.registered_at"
              :date="clientData.client_profile.registered_at"
            />
          </div>
        </div>
      </div>
      
      <div class="stages">
        <div class="row">
          <span
            v-for="n in 6"
            :key="n"
            class="bar"
            :class="n <= getClientFlowCount() ? 'bar--active' : 'bar--inactive'"
          ></span>
        </div>
      </div>
      
      <div class="div-block-94">
        <div class="aktualizacia">
          <span class="aktualizaciaLabel">{{ currentFlowLabel }}</span>
          <span class="aktualizaciaCas">{{ transformDate(clientFlowData.checked_at) }}</span>
        </div>

        <div class="task pointer" @click="toggleClientFlowOption(nextClientFlowItem, true)" v-if="!nextClientFlowItem.invalid">
          <div class="task-copy">
            <div class="task-kicker">Nasledujúca úloha</div>
            <div class="task-label">
              <transition name="fade-slide" mode="out-in">
                <span v-if="showNextFlowLabel" :key="nextClientFlowItem.display_name">
                  {{ nextClientFlowItem.display_name.includes("Kontaktovaný") ? "Kontaktovať" : nextClientFlowItem.display_name }}
                </span>
              </transition>
            </div>
          </div>
          <div class="task-complete" title="Označiť úlohu ako dokončenú">
            <span class="task-check">✓</span>
          </div>
        </div>

        <div v-else></div>
      </div>
    </div>

    <div v-if="notesWithFutureReminder.length > 0" class="notes-wrapper">
      <div class="lastnote" v-for="(note, index) in notesWithFutureReminder" :key="index">
        <span class="mr-3">
          {{ formatDateDDMMYYYY(note.notify_date) }}
        </span>
        <span>
          {{ note.note }}
        </span>
      </div>
    </div>  

    <div class="lastmessage" v-if="clientData.sent_latest_message" @click="openClientChatPopUp">
      <div class="badge" v-if="clientData.sent_unread_messages">
        <div class="text-block-13">
          {{ clientData.sent_unread_messages }}
        </div>
      </div>
      <div>
        {{ clientData.sent_latest_message }}
      </div>
    </div>
  </div>
</template>

<script>
import ClientDistrictBadges from "./ClientDistrictBadges.vue";
import LeadTimeline from "./LeadTimeline.vue";
import clientSource from "../../../utils/clientSource";
import BrokerPickerHandler from "../../shared/PopupHandler/Popups/BrokerPicker/BrokerPickerHandler";
import UserManagerHandler from "../../shared/PopupHandler/Popups/UserManager/UserManagerHandler";
import translateFromDBValue from "../../../mixins/translateFromDBValue";
import {mapGetters} from "vuex";
import {getClientById} from "@/services/brokerService";
import DayBadge from "@/components/broker/KartaKlienti/DayBadge.vue";
import dateConverter from "@/mixins/dateConverter";

const CONTACT_PHASE_FLOW_KEYS = new Set([
  "registrovany",
  "not_contacted",
  "contact_not_successful",
  "contact_not_successful_2",
  "contact_not_successful_3",
  "contact_successful",
]);

const CLIENT_FLOW_PROGRESS = Object.freeze({
  registrovany: 0,
  not_contacted: 0,
  contact_not_successful: 1,
  contact_not_successful_2: 1,
  contact_not_successful_3: 1,
  contact_successful: 1,
  offer_sent: 2,
  meeting_agreed: 3,
  documents_uploaded: 4,
  property_picked: 5,
  financing_picked: 6,
  identity_verified: 6,
  applications_prepared: 6,
  application_signed: 6,
});

export default {
  name: "RegisteredKlientItem",
  components: {
    ClientDistrictBadges,
    LeadTimeline,
    DayBadge,
    UserManagerHandler,
    BrokerPickerHandler
  },
  props: {
    client: Object,
    search: Boolean,
  },
  mixins: [
    translateFromDBValue,
    dateConverter,
  ],
  computed: {
    ...mapGetters({
      refreshGetter: "popupManagerState/getUserManagerRefresh",
      popupSuccessResult: "popupManagerState/getPopupSuccessResult",
      typeByClientProfile: "clientState/getTypeByClientProfile",
    }),

    // 1. Bezpečne vytiahneme pôvod z prvej hypotéky klienta
    clientOrigin() {
      const origin = this.clientData?.client_profile?.origin || null;
      return origin;
    },

    // 2. OPRAVENÉ ROZRADENIE DO KATEGÓRIÍ (Správne poradie podmienok)
    parsedSource() {
      return clientSource(this.clientData);
    },

    nextClientFlowItem() {
      const nextFlow = this.clientFlowData?.flow?.next_success;
      if (!nextFlow) {
        return { invalid: true }
      }
      return this.translateClientFlowKey(nextFlow);
    },

    showDayBadge() {
      return CONTACT_PHASE_FLOW_KEYS.has(this.clientFlowData?.flow?.key);
    },

    currentFlowLabel() {
      return this.translateClientFlowKey(this.clientFlowData?.flow?.key).display_name;
    },

    clientType() {
      return this.typeByClientProfile(this.clientData.client_profile);
    },

    clientFlowData() {
      return this.clientData?.client_profile?.client_flow_status || null;
    },

    notesWithFutureReminder() {
      const futureNotes = [];
      for (const clientNote of this.clientData.client_profile.client_notes) {
        if (this.isFutureDate(clientNote.notify_date)) {
          futureNotes.push(clientNote);
        }
      }
      return futureNotes;
    }
  },
  data: () => ({
    clientFlowChoices: null,
    clientData: null,
    role: null,
    showNextFlowLabel: true,
    flowLabelTimer: null,
  }),
  created () {
    this.clientData = this.client;
    this.clientFlowChoices = this.$store.getters["formStore/getFormByType"]("clientProfileForm").client_flow_status.children.flow.children.key.choices;
    this.role = this.$store.getters["userState/getRole"];
  },
  beforeDestroy () {
    if (this.flowLabelTimer) clearTimeout(this.flowLabelTimer);
  },
  methods: {
    isFutureDate(dateStr) {
      const date = new Date(dateStr);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return date >= today;
    },
    async toggleClientFlowOption(option, verifyContact = false) {
      if (verifyContact && option.value.includes("contact")) {
        this.$store.commit("popupManagerState/pushToPopupOrder", {
          type: null,
          payload: this.clientData.id,
          component: "ClientFlowContactPopUp",
          rules: ['single'],
        });
        return;
      }

      // 1. Spustíme animáciu a schováme starý text
      this.showNextFlowLabel = false;
      
      // Zapneme loading ak treba (voliteľné)
      // this.$store.commit("appState/setLoadingPage", true);

      try {
        // 2. Vykonáme zmenu na serveri
        const patchedProfile = await this.$store.dispatch("clientState/patchClientFlowState", {
          profile: this.clientData.client_profile,
          type: this.clientType,
          flow: {
            key: option.value,
            request_id: window.crypto.randomUUID()
          }
        });
        if (!patchedProfile) throw new Error("Client flow status was not saved");

        // 3. POČKÁME - tu je kľúč k úspechu. 
        // Zdržíme zobrazenie nového textu o cca 500-800ms, aby sa stihla prehrať animácia.
        if (this.flowLabelTimer) clearTimeout(this.flowLabelTimer);
        this.flowLabelTimer = setTimeout(() => {
          this.showNextFlowLabel = true;
          this.$store.commit("appState/setLoadingPage", false);
          this.flowLabelTimer = null;
        }, 1200);
        return true;

      } catch (error) {
        this.showNextFlowLabel = true;
        this.$store.commit("appState/setLoadingPage", false);
        return false;
      }
    },
    transformDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('sk-SK', {
        day: 'numeric', month: 'numeric', year: 'numeric', hour: "numeric", minute: "numeric"
      });
    },
    getClientFlowCount() {
      return CLIENT_FLOW_PROGRESS[this.clientFlowData?.flow?.key] || 0;
    },
    translateClientFlowKey(key) {
      return this.clientFlowChoices.find((ch) => ch.value === key) || {
        value: key,
        display_name: key || "Neznámy stav",
        invalid: !key,
      };
    },
    getContactFlowTransition(contactedResult) {
      if (contactedResult) {
        return {display_name: "Kontaktovaný úspešne", value: "contact_successful"};
      }
      const unsuccessfulTransitions = {
        registrovany: {display_name: "Kontaktovaný neúspešne", value: "contact_not_successful"},
        not_contacted: {display_name: "Kontaktovaný neúspešne", value: "contact_not_successful"},
        contact_not_successful: {display_name: "Kontaktovaný neúspešne 2x", value: "contact_not_successful_2"},
        contact_not_successful_2: {display_name: "Kontaktovaný neúspešne 3x", value: "contact_not_successful_3"},
      };
      return unsuccessfulTransitions[this.clientFlowData?.flow?.key] || null;
    },
    openClientChatPopUp () {
      this.clientData.sent_unread_messages = 0;
      this.$store.commit("popupManagerState/pushToPopupOrder", {
        type: null,
        payload: this.client,
        component: "ClientChatPopUp",
        rules: null,
      });
    },
  },
  watch: {
    async popupSuccessResult(result) {
      if (!result || typeof result !== "object") return;
      if (this.clientData.id !== result.client || !Object.prototype.hasOwnProperty.call(result, "contactedResult")) return;

      const transition = this.getContactFlowTransition(result.contactedResult);
      if (!transition) {
        this.$store.commit("popupManagerState/setPopupSuccessResult", null);
        return;
      }

      const shouldArchive = transition.value === "contact_not_successful_3";
      const changed = await this.toggleClientFlowOption(transition, false);
      if (changed && shouldArchive) {
        await this.$store.dispatch("clientState/archiveClient", {
          type: this.clientType,
          clientProfileId: this.clientData.client_profile.id,
          reason: "not_available"
        });
      }
      this.$store.commit("popupManagerState/setPopupSuccessResult", null);
    },
    async refreshGetter(refresh) {
      if (!this.search || !refresh) return;
      this.clientData = await getClientById(this.clientData.id);
    },
    client (newClient) {
      this.clientData = newClient;
    },
  },
};
</script>

<style scoped lang="scss">
.klient-item {
  padding: 14px 18px;
}

.klient-item-to-part {
  min-height: 76px;
  grid-template-columns: minmax(230px, 0.9fr) minmax(160px, 0.65fr) minmax(360px, 1.35fr);
  gap: 22px;
}

.admin-client-item .klient-item-to-part {
  grid-template-columns: minmax(280px, 1fr) minmax(150px, 0.55fr) minmax(360px, 1.35fr);
}

.client-summary,
.klient-info-wrapper {
  min-width: 0;
}

.client-identity-row {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
}

.client-copy {
  min-width: 0;
  flex: 1;
}

.client-identity-row--admin {
  gap: 14px;
}

.assigned-broker,
.client-copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 4px;
}

.assigned-broker {
  flex: 0 0 auto;
}

.identity-kicker {
  color: var(--app-text-muted);
  font-size: 8px;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.assigned-broker-avatar {
  display: grid;
  width: 44px;
  height: 44px;
  place-items: center;
  border: 1px solid var(--app-border);
  border-radius: 13px;
  background: var(--app-surface-muted);
}

::v-deep .assigned-broker-avatar .klienticta {
  margin: 0;
}

::v-deep .assigned-broker-avatar .broker-image {
  width: 36px;
  height: 36px;
  border: 2px solid var(--app-surface);
  object-fit: cover;
}

.stages {
  height: auto;
  padding: 0;
}

.div-block-94 {
  grid-template-columns: minmax(135px, 0.65fr) minmax(210px, 1fr);
  align-items: center;
  gap: 14px;
  margin: 0;
}

.fade-slide-enter-active {
  transition: all 0.4s ease-out;
}

.fade-slide-leave-active {
  transition: all 0.3s ease-in;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(10px); // Nový text príde zdola
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px); // Starý text odíde nahor
}

.row {
  display: flex;
  gap: 8px;
  padding: 8px 0;
}

.bar {
  height: 4px;
  border-radius: 999px;
  flex: 1;
}

.bar--active {
  background: var(--app-primary);
}

.bar--inactive {
  background: var(--app-surface-strong);
  opacity: 0.6;
}

.aktualizacia {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.aktualizaciaLabel {
  color: var(--app-text);
  font-size: 10px;
  font-weight: 700;
}

.aktualizaciaCas {
  color: var(--app-text-muted);
  font-size: 9px;
}

.task {
  display: flex;
  min-width: 0;
  min-height: 54px;
  padding: 8px 10px 8px 13px;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid var(--app-border);
  border-radius: 12px;
  background: var(--app-surface-muted);
  color: var(--app-text);
  transition: border-color 0.18s ease, background 0.18s ease, transform 0.18s ease;
}

.task:hover {
  border-color: var(--app-primary);
  background: var(--app-primary-soft);
  transform: translateY(-1px);
}

.task-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.task-kicker {
  color: var(--app-text-muted);
  font-size: 8px;
  font-weight: 700;
  text-transform: uppercase;
}

.task-label {
  overflow: hidden;
  color: var(--app-text);
  font-size: 11px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.task-complete {
  position: relative;
  display: inline-flex;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid var(--app-primary);
  border-radius: 50%;
  background: var(--app-primary-soft);
  color: var(--app-primary);
}

.task-check {
  font-size: 15px;
  font-weight: 900;
}

/* --- NOVÝ ŠTÝL PRE SOURCE BADGE --- */

.client-badges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.client-badges:empty {
  display: none;
}

.source-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;

  margin: 0;

  padding: 2px 8px;

  border-radius: 6px;

  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.2px;

  text-decoration: none;
  line-height: 1.1;

  backdrop-filter: blur(4px);

  transition: all 0.15s ease;

  &:hover {
    opacity: 0.85;
  }

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 999px;
    flex-shrink: 0;
  }

  &.source-organika {
    background: rgba(52, 120, 246, 0.06);
    color: #4d6fa3;

    &::before {
      background: #7ea6e8;
    }
  }

  &.source-ads {
    background: rgba(52, 120, 246, 0.1);
    color: #2f5f98;

    &::before {
      background: #3478f6;
    }
  }

  &.source-dev {
    background: rgba(52, 120, 246, 0.05);
    color: #6b7280;

    text-transform: none;

    &::before {
      background: #9ca3af;
    }
  }

  &.source-default {
    background: rgba(0, 0, 0, 0.03);
    color: #6b7280;

    &::before {
      background: #9ca3af;
    }
  }
}

::v-deep .day-badge {
  min-width: 58px;
  margin: 0;
  padding: 5px 10px;
  flex: 0 0 auto;
}

@media screen and (max-width: 1180px) {
  .klient-item-to-part,
  .admin-client-item .klient-item-to-part {
    grid-template-columns: minmax(230px, 1fr) minmax(180px, 0.75fr);
  }

  .div-block-94 {
    grid-column: 1 / -1;
  }
}

@media screen and (max-width: 767px) {
  .klient-item-to-part,
  .admin-client-item .klient-item-to-part {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .div-block-94 {
    grid-column: auto;
    grid-template-columns: 1fr;
  }

  .aktualizacia {
    align-items: flex-start;
  }
}
</style>
